import type { StrategyLayer, StrategyLayerKind } from "@/types/strategy";

// ── Modifier rules ─────────────────────────────────────────────────────────
// Y=0 flip (side: long/short) — flips profit/loss. Always a shape change.
// X-flip (direction, optionType) — included when curve is asymmetric around spot.
// Excluded for butterfly, long_condor, short_condor (call/put = same P&L at expiry).

/** Strategies with a `side` toggle (long/short). */
const SIDE_KINDS = new Set<StrategyLayerKind>([
  "single_call",
  "single_put",
  "iron_fly",
  "iron_condor",
  "butterfly",
  "straddle",
  "strangle",
  "guts",
  "synthetic_future",
  "combo",
]);

/** Strategies where `optionType` changes curve direction (asymmetric around spot). */
const OPTION_TYPE_KINDS = new Set<StrategyLayerKind>([
  "ratio_backspread",
  "ratio_spread",
  "time_spread",
]);

/**
 * Stable identity for performance aggregation. It intentionally ignores
 * ticker, DTE, width, strikes, saved-copy IDs, and position IDs. Those are
 * evidence about one use of a strategy, not the strategy itself.
 */
export function strategyIdentityKey(layers: readonly StrategyLayer[]): string {
  return layers
    .filter((layer) => layer.enabled !== false)
    .map((layer) => {
      const params = layer.params ?? {};
      const modifiers: string[] = [];

      if (SIDE_KINDS.has(layer.kind) && params.side != null) {
        modifiers.push(params.side >= 1 ? "long" : "short");
      }
      if (layer.kind === "broken_wing_butterfly") {
        modifiers.push(params.direction === -1 ? "lower" : "upper");
      } else if (layer.kind === "jade_lizard") {
        modifiers.push(params.direction === -1 ? "reverse" : "standard");
      }
      if (OPTION_TYPE_KINDS.has(layer.kind)) {
        modifiers.push(params.optionType >= 1 ? "put" : "call");
      }

      return [layer.kind, ...modifiers].join(":");
    })
    .join("+");
}

export interface NamingLabels {
  /** Kind key → localized display name (e.g., "iron_fly" → "Iron Fly") */
  kinds: Record<string, string>;
  long: string;
  short: string;
  upper: string;
  lower: string;
  reverse: string;
  call: string;
  put: string;
  newStrategy: string;
  /** Display name for an unnamed template with no layers yet (tab strip). */
  newTab: string;
}

function usesCompactModifier(labels: NamingLabels): boolean {
  return /[\u3400-\u9fff]/.test(
    [labels.long, labels.short, labels.upper, labels.lower, labels.reverse]
      .filter(Boolean)
      .join(""),
  );
}

function modifierPrefix(layer: StrategyLayer, labels: NamingLabels): string {
  const { kind } = layer;
  // Defensive against partial layer rows (e.g. AI-grounding template fetches
  // that only select `kind` + `enabled`). All access below is read-only.
  const params: Record<string, number> = layer.params ?? {};
  const parts: string[] = [];

  if (SIDE_KINDS.has(kind) && params.side != null) {
    parts.push(params.side >= 1 ? labels.long : labels.short);
  }

  if (kind === "broken_wing_butterfly") {
    parts.push(params.direction === -1 ? labels.lower : labels.upper);
  } else if (kind === "jade_lizard" && params.direction === -1) {
    parts.push(labels.reverse);
  }

  if (OPTION_TYPE_KINDS.has(kind)) {
    parts.push(params.optionType >= 1 ? labels.put : labels.call);
  }

  if (parts.length === 0) return "";
  const separator = usesCompactModifier(labels) ? "" : " ";
  return parts.join(separator) + separator;
}

function layerDisplayName(layer: StrategyLayer, labels: NamingLabels): string {
  return (
    modifierPrefix(layer, labels) + (labels.kinds[layer.kind] ?? layer.kind)
  );
}

/**
 * English-locale `NamingLabels` for non-React callers (server route handlers,
 * Zustand store snapshots, AI grounding context). Mirrors `useNamingLabels()`
 * in en-US so the AI sees the same auto-name strings a US user sees in the
 * simulator. Kind labels come from a snake_case → Title Case derivation with
 * two carve-outs (`single_call` / `single_put` collapse to "Call" / "Put").
 */
const KIND_OVERRIDES: Record<string, string> = {
  single_call: "Call",
  single_put: "Put",
};

function snakeToTitle(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export const ENGLISH_NAMING_LABELS: NamingLabels = {
  kinds: new Proxy(
    {},
    {
      get: (_, key: string) => KIND_OVERRIDES[key] ?? snakeToTitle(key),
    },
  ),
  long: "Long",
  short: "Short",
  upper: "Upper",
  lower: "Lower",
  reverse: "Reverse",
  call: "Call",
  put: "Put",
  newStrategy: "New Strategy",
  newTab: "New Tab",
};

/** Append a "(N)" suffix to `baseName` when it would collide with an existing name. */
export function deduplicateName(
  baseName: string,
  existingNames: string[],
): string {
  if (!existingNames.includes(baseName)) return baseName;
  let n = 2;
  while (existingNames.includes(`${baseName} (${n})`)) n++;
  return `${baseName} (${n})`;
}

/**
 * The un-deduplicated base name for a layer set. Use this when you need to
 * group templates by their auto-name (e.g. tab-strip dedup that suffixes
 * positionally rather than by collision count).
 */
export function strategyBaseName(
  layers: StrategyLayer[],
  labels: NamingLabels,
): string {
  if (layers.length === 0) return labels.newStrategy;
  return layers.map((l) => layerDisplayName(l, labels)).join(" + ");
}

/**
 * Generate a strategy name from its layers.
 *
 * Pure function — all localized strings are passed in via `labels`.
 * Callers use `te()` / `useKindLabels()` to build the labels object.
 */
export function generateStrategyName(
  layers: StrategyLayer[],
  labels: NamingLabels,
  existingNames: string[],
): string {
  return deduplicateName(strategyBaseName(layers, labels), existingNames);
}
